import axios from 'axios';
import { BASE_URL } from '../../../utils/config';
import { objectToCamelCase } from '../../../utils/objectTransforms';

const EVALUATION_API = `/evaluations/v1/agentevaluations`;
const EVALUATION_TEMPLATES_API = `/evaluations/v1/templates`;

export interface EvaluationQuestionUi {
    options?: EvaluationValue[];
}

export interface EvaluationQuestion {
    uuid?: string;
    key?: string;
    kind?: string;
    ui?: EvaluationQuestionUi;
    options?: EvaluationValue[];
    [key: string]: unknown;
}

export interface EvaluationTemplate {
    id: string | number;
    title?: string;
    questionnaire?: EvaluationQuestion[];
    [key: string]: unknown;
}

export interface EvaluationUser {
    firstName?: string;
    lastName?: string;
    personalUsername?: string;
    email?: string;
    id?: string | number;
}

export type EvaluationValue = string | number | undefined;
export type EvaluationData = Record<string, EvaluationValue>;

export interface EvaluationItem {
    id: string | number;
    templateId: string | number;
    evaluation: EvaluationData;
    createdAt: string;
    createdByUser?: EvaluationUser;
    createdBy?: string | number | null;
    evaluatedUserId?: string | number | null;
    [key: string]: unknown;
}

type EvaluationTemplatesQuery = Record<string, string | number | boolean>;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isQuestion(value: unknown): value is EvaluationQuestion {
    return isRecord(value);
}

function toTemplate(value: unknown): EvaluationTemplate {
    const formatted = objectToCamelCase(value) as EvaluationTemplate;
    const questionnaire = Array.isArray(formatted.questionnaire) ? formatted.questionnaire.filter(isQuestion) : [];
    const toOptions = (options: unknown): EvaluationValue[] =>
        Array.isArray(options)
            ? options.filter((item): item is EvaluationValue => typeof item === 'string' || typeof item === 'number' || item === undefined)
            : [];

    return {
        ...formatted,
        questionnaire: questionnaire.map(question => ({
            ...question,
            key: typeof question.uuid === 'string' ? question.uuid : question.key,
            options: toOptions(question.ui?.options)
        }))
    };
}

function toEvaluationItem(value: unknown): EvaluationItem {
    const formatted = objectToCamelCase(value) as EvaluationItem;
    const rawValue = isRecord(value) ? value.evaluation : undefined;
    const evaluation = isRecord(rawValue)
        ? (Object.fromEntries(
              Object.entries(rawValue).filter(([, item]) => typeof item === 'string' || typeof item === 'number' || item === undefined)
          ) as EvaluationData)
        : {};

    return {
        ...formatted,
        evaluation
    };
}

export async function fetchEvaluationsTemplatesData(
    authToken: string,
    params: EvaluationTemplatesQuery = {}
): Promise<EvaluationTemplate[]> {
    try {
        const response = await axios.get(`${BASE_URL}${EVALUATION_TEMPLATES_API}`, {
            params,
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        });

        const { data } = response;

        return Array.isArray(data) ? data.map(toTemplate) : [];
    } catch (error) {
        console.error('Error fetching evaluation templates:', error);
        return [];
    }
}

export async function fetchEvaluationsData(
    authToken: string,
    params = {
        limit: 10
    }
): Promise<EvaluationItem[]> {
    try {
        const response = await axios.get(`${BASE_URL}${EVALUATION_API}`, {
            params,
            headers: {
                Authorization: `Bearer ${authToken}`
            }
        });

        return Array.isArray(response.data) ? response.data.map(toEvaluationItem) : [];
    } catch (error) {
        console.error('Error fetching evaluations:', error);
        return [];
    }
}
